import { useEffect, useRef, useState } from 'react';
import { Box } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { facilityAssetId, facilityForAntenna } from '@/features/catalogue/facilities';
import { registerAntennaTwin, registerMissionTwin } from '@/features/twin/data';
import { getGlobeEngine } from '../globe/engineHandle';
import { useLoader } from '../store';

/** One DOM action follows the selected label; no extra geometry or per-satellite UI. */
export function SelectedTwinAction() {
  const button = useRef<HTMLButtonElement>(null);
  const dataset = useLoader((s) => s.dataset);
  const selection = useUI((s) => s.selection);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const available =
    dataset?.missions.some((m) => m.id === selection.missionId) ||
    dataset?.tdrss.some((m) => m.id === selection.missionId) ||
    dataset?.antennas.some((a) => a.id === selection.antennaId);

  useEffect(() => {
    if (!available) return;
    let frame = 0;
    const position = () => {
      const element = button.current;
      const engine = getGlobeEngine();
      const picked = engine?.getSelection();
      const matches = picked?.id === (picked?.kind === 'antenna' ? selection.antennaId : selection.missionId);
      const anchor = matches ? engine?.getSelectionAnchor?.() : null;
      if (element) {
        const width = element.offsetWidth;
        const parentWidth = element.parentElement?.clientWidth ?? 0;
        const parentHeight = element.parentElement?.clientHeight ?? 0;
        const x = anchor ? anchor.x - (anchor.centered ? width / 2 : 0) : 0;
        const visible =
          anchor &&
          x >= 0 &&
          x + width <= parentWidth &&
          anchor.y >= 0 &&
          anchor.y + element.offsetHeight <= parentHeight;
        element.style.visibility = visible ? 'visible' : 'hidden';
        if (anchor) element.style.transform = `translate(${Math.round(x)}px, ${Math.round(anchor.y)}px)`;
      }
      frame = requestAnimationFrame(position);
    };
    frame = requestAnimationFrame(position);
    return () => cancelAnimationFrame(frame);
  }, [available, selection.missionId, selection.antennaId]);

  if (!available || !dataset) return null;
  return (
    <button
      ref={button}
      type="button"
      data-testid="selected-twin-action"
      disabled={busy}
      style={{ visibility: 'hidden' }}
      className="absolute left-0 top-0 z-10 flex items-center gap-1 rounded border border-accent/40 bg-raised/95 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-accent shadow-sm backdrop-blur hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={async (e) => {
        e.stopPropagation();
        const picked = getGlobeEngine()?.getSelection();
        if (!picked) return;
        setBusy(true);
        try {
          let assetId: string | undefined;
          if (['mission', 'tdrs', 'horizons'].includes(picked.kind)) {
            const mission = dataset.missions.find((m) => m.id === picked.id) ?? dataset.tdrss.find((m) => m.id === picked.id);
            if (mission) assetId = await registerMissionTwin(mission, dataset.network);
          } else if (picked.kind === 'antenna') {
            const antenna = dataset.antennas.find((a) => a.id === picked.id);
            const facility = picked.stationId && facilityForAntenna(dataset.network, picked.id);
            if (facility) assetId = facilityAssetId(facility.id);
            else if (antenna) assetId = registerAntennaTwin(antenna, dataset.network);
          }
          if (!assetId || useLoader.getState().network !== dataset.network) return;
          useUI.getState().select({ assetId, subsystemId: null });
          navigate(`/twin/${encodeURIComponent(assetId)}`);
        } catch (error) {
          toast(
            'error',
            `Could not open the digital twin: ${error instanceof Error ? error.message : String(error)}`,
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      <Box aria-hidden className="h-3 w-3" />
      {busy ? 'Opening twin…' : 'View digital twin'}
    </button>
  );
}
